import React, { useEffect, useState, useMemo } from "react";
import { 
    Card, 
    Grid, 
    InputLabel, 
    Autocomplete, 
    TextField, 
    Switch, 
    Icon, 
    CircularProgress, 
    Chip,
    Avatar,
    Tooltip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Collapse,
    IconButton,
    Menu,
    MenuItem,
    Select,
    Pagination,
    InputAdornment
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { useUser } from "context/UserContext";
import { get, post } from "utils/WebService";
import MDSnackbar from "components/MDSnackbar";
import MDAuditModal from "components/MDAuditModal";
import { ESCURO, corRoxo } from "assets/coresTema";

const STATUS_COLORS = [
    "#3b82f6", // Blue (Não Localizado)
    "#a855f7", // Purple (Em Cobrança)
    "#f97316", // Orange (Tratativas Cobrança)
    "#06b6d4", // Cyan (Sem Interesse)
    "#eab308", // Yellow (Em Pesquisa)
    "#22c55e", // Green (Acordo)
    "#ec4899", // Pink
    "#14b8a6", // Teal
    "#6366f1", // Indigo
    "#ef4444"  // Red
];

export default function FilaProcessamento() {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const { userName } = useUser();

    const [regras, setRegras] = useState([]);
    const [operadores, setOperadores] = useState([]);
    const [selectedRegra, setSelectedRegra] = useState(null);
    const [selectedOperadores, setSelectedOperadores] = useState([]);
    const [selectedOperadorAusente, setSelectedOperadorAusente] = useState(null);
    const [ignorarPropriedade, setIgnorarPropriedade] = useState(false);
    const [limparFila, setLimparFila] = useState(true);
    const [respeitarAgendamento, setRespeitarAgendamento] = useState(true);

    const [loading, setLoading] = useState(false);
    const [successSB, setSuccessSB] = useState(false);
    const [errorSB, setErrorSB] = useState(false);
    const [sbMessage, setSbMessage] = useState("");
    const [resultados, setResultados] = useState([]);
    const [timestampDistribuicao, setTimestampDistribuicao] = useState("");

    // Informativo de Regra vs Processamento
    const [previewData, setPreviewData] = useState(null);
    const [loadingPreview, setLoadingPreview] = useState(false);

    // Controle de Tabela e Visualização Estilo Mockup
    const [searchFilter, setSearchFilter] = useState("");
    const [sortBy, setSortBy] = useState("nome_asc");
    const [expandedRows, setExpandedRows] = useState({});
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [exportAnchorEl, setExportAnchorEl] = useState(null);

    const [openAuditModal, setOpenAuditModal] = useState(false);
    const [auditLogs, setAuditLogs] = useState([]);
    const [loadingAudit, setLoadingAudit] = useState(false);

    useEffect(() => {
        if (!selectedRegra || !selectedOperadores || selectedOperadores.length === 0) {
            setPreviewData(null);
            return;
        }

        const payload = {
            regraId: selectedRegra.id,
            userIds: selectedOperadores,
            operadorAusenteId: selectedOperadorAusente ? selectedOperadorAusente.id : null,
            ignorarPropriedade: ignorarPropriedade,
            respeitarAgendamento: respeitarAgendamento
        };

        setLoadingPreview(true);
        post("/regra-fila/preview-processamento", payload)
            .then((res) => {
                if (res) {
                    setPreviewData(res);
                } else {
                    setPreviewData(null);
                }
            })
            .catch(() => setPreviewData(null))
            .finally(() => setLoadingPreview(false));
    }, [selectedRegra, selectedOperadores, selectedOperadorAusente, ignorarPropriedade, respeitarAgendamento]);

    const handleOpenAudit = () => {
        setOpenAuditModal(true);
        setLoadingAudit(true);
        get("/regra-fila/processamento/historico-geral")
            .then((res) => {
                if (res && Array.isArray(res)) {
                    setAuditLogs(res);
                } else {
                    setAuditLogs([]);
                }
            })
            .catch(() => setAuditLogs([]))
            .finally(() => setLoadingAudit(false));
    };

    const getTextFieldStyle = () => ({}); // aparência dos campos vem do tema (assets/coresTema)

    useEffect(() => {
        get("/regra-fila/list-all").then((res) => {
            if (res && Array.isArray(res)) {
                setRegras(res.filter(r => r.ativo));
            } else if (res) {
                setRegras(Array.isArray(res) ? res : []);
            }
        }).catch(() => setRegras([]));

        get("/users/list-all-operators").then((res) => {
            if (res) {
                setOperadores(res.list || res.data || (Array.isArray(res) ? res : []));
            }
        }).catch(() => setOperadores([]));
    }, []);

    const handleLimparFiltros = () => {
        setSelectedRegra(null);
        setSelectedOperadores([]);
        setSelectedOperadorAusente(null);
        setIgnorarPropriedade(false);
        setLimparFila(true);
        setRespeitarAgendamento(true);
        setPreviewData(null);
        setResultados([]);
        setExpandedRows({});
        setSbMessage("Filtros e parâmetros limpos com sucesso.");
        setSuccessSB(true);
    };

    const handleProcessar = () => {
        if (!selectedRegra) {
            setSbMessage("Selecione uma regra para processamento.");
            setErrorSB(true);
            return;
        }

        if (!selectedOperadores || selectedOperadores.length === 0) {
            setSbMessage("Selecione pelo menos um operador para receber os casos.");
            setErrorSB(true);
            return;
        }

        setLoading(true);

        const payload = {
            regraId: selectedRegra.id,
            userIds: selectedOperadores,
            operadorAusenteId: selectedOperadorAusente ? selectedOperadorAusente.id : null,
            ignorarPropriedade: ignorarPropriedade,
            limparFila: limparFila,
            respeitarAgendamento: respeitarAgendamento
        };

        post("/regra-fila/processar", payload)
            .then((res) => {
                if (res && Array.isArray(res)) {
                    setResultados(res);
                    const now = new Date();
                    const formatted = now.toLocaleDateString("pt-BR") + " " + now.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
                    setTimestampDistribuicao(formatted);
                    setExpandedRows({});
                    setSbMessage("Processamento concluído com sucesso!");
                } else {
                    setResultados([]);
                    setSbMessage("Nenhum caso encontrado para os operadores selecionados.");
                }
                
                setSuccessSB(true);
                // Mantemos os parâmetros selecionados na tela para facilitar reprocessamento ou ajustes
            })
            .catch(() => {
                setSbMessage("Erro ao processar fila.");
                setErrorSB(true);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const summaryKPIs = useMemo(() => {
        if (!resultados || resultados.length === 0) {
            return { totalOperadores: 0, totalAntes: 0, totalRetirados: 0, totalAdicionados: 0, totalDepois: 0 };
        }
        const totalOperadores = resultados.length;
        const totalAntes = resultados.reduce((acc, r) => acc + (r.antes || 0), 0);
        const totalRetirados = resultados.reduce((acc, r) => acc + (r.retirados || 0), 0);
        const totalAdicionados = resultados.reduce((acc, r) => acc + (r.adicionados || 0), 0);
        const totalDepois = resultados.reduce((acc, r) => acc + (r.depois || 0), 0);
        return { totalOperadores, totalAntes, totalRetirados, totalAdicionados, totalDepois };
    }, [resultados]);

    const filteredAndSortedResultados = useMemo(() => {
        if (!resultados || resultados.length === 0) return [];
        let list = [...resultados];
        if (searchFilter.trim()) {
            const term = searchFilter.trim().toLowerCase();
            list = list.filter(r => (r.operador || "").toLowerCase().includes(term));
        }

        if (sortBy === "nome_asc") {
            list.sort((a, b) => (a.operador || "").localeCompare(b.operador || ""));
        } else if (sortBy === "nome_desc") {
            list.sort((a, b) => (b.operador || "").localeCompare(a.operador || ""));
        } else if (sortBy === "depois_desc") {
            list.sort((a, b) => (b.depois || 0) - (a.depois || 0));
        } else if (sortBy === "adicionados_desc") {
            list.sort((a, b) => (b.adicionados || 0) - (a.adicionados || 0));
        } else if (sortBy === "retirados_desc") {
            list.sort((a, b) => (b.retirados || 0) - (a.retirados || 0));
        }

        return list;
    }, [resultados, searchFilter, sortBy]);

    const paginatedResultados = useMemo(() => {
        if (rowsPerPage === -1) return filteredAndSortedResultados;
        const start = page * rowsPerPage;
        return filteredAndSortedResultados.slice(start, start + rowsPerPage);
    }, [filteredAndSortedResultados, page, rowsPerPage]);

    const toggleRowExpand = (idx) => {
        setExpandedRows(prev => ({
            ...prev,
            [idx]: !prev[idx]
        }));
    };

    const handleExportCSV = () => {
        if (!resultados || resultados.length === 0) return;
        setExportAnchorEl(null);
        let csvContent = "data:text/csv;charset=utf-8,\uFEFF";
        csvContent += "Operador;Fila Anterior;Fichas Retiradas;Fichas Adicionadas;Fila Atual;Status\n";
        resultados.forEach(r => {
            csvContent += `"${r.operador || ''}";${r.antes || 0};${r.retirados || 0};${r.adicionados || 0};${r.depois || 0};Concluído\n`;
        });
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `distribuicao_filas_${new Date().getTime()}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setSbMessage("Resultado da distribuição exportado em CSV com sucesso!");
        setSuccessSB(true);
    };

    const respNome = userName || sessionStorage.getItem("userName") || "Aline Bombonato";
    const dataDisp = timestampDistribuicao || (new Date().toLocaleDateString("pt-BR") + " " + new Date().toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }));

    return (
        <Grid container display="flex" justifyContent="center">
            <Grid item xs={12} md={12}>
                {/* Form de Filtros e Processamento */}
                <Card variant="outlined" sx={{ p: 2, mb: 3, borderRadius: "12px", background: darkMode ? ESCURO.cartao : "#fafafa", borderColor: darkMode ? "rgba(255, 255, 255, 0.12)" : "#e0e0e0" }}>
                    <Grid container spacing={3} alignItems="flex-start">
                        {/* Coluna 1: Campos de Seleção */}
                        <Grid item xs={12} md={6}>
                            <MDBox display="flex" flexDirection="column" gap={2.5}>
                                <MDBox>
                                    <MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "text"} display="block" mb={0.5}>
                                        Regra de Ordenação
                                    </MDTypography>
                                    <Autocomplete
                                         options={regras}
                                         getOptionLabel={(option) => {
                                             if (!option) return "";
                                             const eqLabel = option.equipeNome ? ` (${option.equipeNome})` : " (Geral)";
                                             return `${option.nome || ""}${eqLabel}`;
                                         }}
                                         value={selectedRegra}
                                         onChange={(event, newValue) => setSelectedRegra(newValue)}
                                         renderInput={(params) => (
                                             <TextField
                                                 {...params}
                                                 variant="outlined"
                                                 size="small"
                                                 placeholder={!selectedRegra ? "Selecione a Regra..." : ""}
                                                 fullWidth
                                                 sx={getTextFieldStyle(darkMode)}
                                             />
                                         )}
                                         fullWidth
                                     />
                                </MDBox>

                                <MDBox>
                                    <MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "text"} display="block" mb={0.5}>
                                        Operadores Destino (Receberão os casos na fila)
                                    </MDTypography>
                                    <Autocomplete
                                         multiple
                                         options={operadores}
                                         getOptionLabel={(option) => option.nome || option.ccNome || option.id || ""}
                                         value={operadores.filter(op => selectedOperadores.includes(op.id))}
                                         onChange={(event, newValue) => {
                                             setSelectedOperadores(newValue ? newValue.map(v => v.id) : []);
                                         }}
                                         isOptionEqualToValue={(option, value) => option.id === value?.id}
                                         renderTags={(value, getTagProps) =>
                                             value.map((option, index) => (
                                                 <Chip
                                                     key={option.id}
                                                     label={option.nome || option.ccNome || option.id || ""}
                                                     size="small"
                                                     color="info"
                                                     {...getTagProps({ index })}
                                                     sx={{ m: 0.3, my: 0.3 }}
                                                 />
                                             ))
                                         }
                                         renderInput={(params) => (
                                             <TextField
                                                 {...params}
                                                 variant="outlined"
                                                 size="small"
                                                 placeholder={selectedOperadores.length === 0 ? "Selecione os operadores..." : ""}
                                                 fullWidth
                                                 sx={getTextFieldStyle(darkMode)}
                                             />
                                         )}
                                         fullWidth
                                         noOptionsText="Nenhum operador encontrado"
                                     />
                                </MDBox>

                                <MDBox>
                                    <MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "text"} display="block" mb={0.5}>
                                        Operador Ausente / Faltou (Distribuição por Ausência - Opcional)
                                    </MDTypography>
                                    <Autocomplete
                                         options={operadores}
                                         getOptionLabel={(option) => option.nome || option.ccNome || option.id || ""}
                                         value={selectedOperadorAusente}
                                         onChange={(event, newValue) => setSelectedOperadorAusente(newValue)}
                                         isOptionEqualToValue={(option, value) => option.id === value?.id}
                                         renderInput={(params) => (
                                             <TextField
                                                 {...params}
                                                 variant="outlined"
                                                 size="small"
                                                 placeholder={!selectedOperadorAusente ? "Selecione o operador ausente (opcional)..." : ""}
                                                 fullWidth
                                                 sx={getTextFieldStyle(darkMode)}
                                             />
                                         )}
                                         fullWidth
                                         noOptionsText="Nenhum operador encontrado"
                                     />
                                    <MDTypography variant="caption" display="block" mt={0.5} sx={{ color: darkMode ? "#cbd5e1" : "text.secondary" }}>
                                        Processa e distribui a fila dos operadores selecionados e redistribui simultaneamente os casos do operador ausente.
                                    </MDTypography>
                                </MDBox>
                            </MDBox>
                        </Grid>

                        {/* Coluna 2: Opções com Switches */}
                        <Grid item xs={12} md={6}>
                            <MDBox 
                                p={2} 
                                sx={{ 
                                    backgroundColor: darkMode ? ESCURO.painel : "#f8f9fa", 
                                    borderRadius: "10px", 
                                    border: "1px solid", 
                                    borderColor: darkMode ? "rgba(255, 255, 255, 0.1)" : "#e9ecef",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 2
                                }}
                            >
                                <MDBox display="flex" alignItems="flex-start">
                                    <Switch 
                                        checked={ignorarPropriedade}
                                        onChange={(e) => setIgnorarPropriedade(e.target.checked)}
                                        color="info"
                                        sx={{ mt: -0.5 }}
                                    />
                                    <MDBox ml={1} onClick={() => setIgnorarPropriedade(!ignorarPropriedade)} sx={{ cursor: "pointer", userSelect: "none" }}>
                                        <MDTypography variant="button" fontWeight="medium" color={darkMode ? "white" : "text"} display="block">
                                            Não olhar a propriedade
                                        </MDTypography>
                                        <MDTypography variant="caption" sx={{ color: darkMode ? "#cbd5e1" : "text.secondary" }}>
                                            Distribuir aleatoriamente entre os operadores selecionados
                                        </MDTypography>
                                    </MDBox>
                                </MDBox>

                                <MDBox display="flex" alignItems="flex-start">
                                    <Switch 
                                        checked={limparFila}
                                        onChange={(e) => setLimparFila(e.target.checked)}
                                        color="info"
                                        sx={{ mt: -0.5 }}
                                    />
                                    <MDBox ml={1} onClick={() => setLimparFila(!limparFila)} sx={{ cursor: "pointer", userSelect: "none" }}>
                                        <MDTypography variant="button" fontWeight="medium" color={darkMode ? "white" : "text"} display="block">
                                            Limpar fila atual antes de processar
                                        </MDTypography>
                                        <MDTypography variant="caption" sx={{ color: darkMode ? "#cbd5e1" : "text.secondary" }}>
                                            Se desmarcado, as novas fichas são apenas acrescentadas e reordenadas na fila atual
                                        </MDTypography>
                                    </MDBox>
                                </MDBox>

                                <MDBox display="flex" alignItems="flex-start">
                                    <Switch 
                                        checked={respeitarAgendamento}
                                        onChange={(e) => setRespeitarAgendamento(e.target.checked)}
                                        color="info"
                                        sx={{ mt: -0.5 }}
                                    />
                                    <MDBox ml={1} onClick={() => setRespeitarAgendamento(!respeitarAgendamento)} sx={{ cursor: "pointer", userSelect: "none" }}>
                                        <MDTypography variant="button" fontWeight="medium" color={darkMode ? "white" : "text"} display="block">
                                            Respeitar agendamento futuro
                                        </MDTypography>
                                        <MDTypography variant="caption" sx={{ color: darkMode ? "#cbd5e1" : "text.secondary" }}>
                                            Não distribuir casos no dia que estão agendados para datas futuras (posteriores a hoje)
                                        </MDTypography>
                                    </MDBox>
                                </MDBox>
                            </MDBox>

                            {/* Botões de Ação */}
                            <Grid item xs={12} display="flex" justifyContent="flex-end" alignItems="center" mt={3} gap={1.5}>
                                <MDButton 
                                    variant="contained" 
                                    color="info" 
                                    size="medium" 
                                    onClick={handleLimparFiltros}
                                    disabled={loading}
                                    sx={{ 
                                        color: darkMode ? "#cbd5e1" : "#475569",
                                        borderColor: darkMode ? "rgba(255, 255, 255, 0.25)" : "#cbd5e1",
                                        "&:hover": {
                                            borderColor: darkMode ? "#ffffff" : "#0288d1",
                                            color: darkMode ? "#ffffff" : "#0288d1",
                                            bgcolor: darkMode ? "rgba(255, 255, 255, 0.05)" : "rgba(2, 136, 209, 0.04)"
                                        }
                                    }}
                                >
                                    <Icon sx={{ mr: 0.5 }}>cleaning_services</Icon>
                                    Limpar Filtros
                                </MDButton>
                                <MDButton 
                                    variant="outlined" 
                                    color="info" 
                                    size="medium" 
                                    onClick={handleOpenAudit}
                                >
                                    <Icon sx={{ mr: 0.5 }}>history</Icon>
                                    Auditoria de Distribuição
                                </MDButton>
                                <MDButton 
                                    variant="contained" 
                                    color="info" 
                                    size="medium" 
                                    onClick={handleProcessar}
                                    disabled={loading}
                                    sx={{ 
                                        px: 3,
                                        backgroundColor: darkMode ? "#007bff !important" : undefined,
                                        color: "#ffffff !important",
                                        fontWeight: "bold",
                                        boxShadow: darkMode ? "0 4px 10px rgba(0, 123, 255, 0.3)" : undefined,
                                        "&:hover": {
                                            backgroundColor: darkMode ? "#0069d9 !important" : undefined
                                        }
                                    }}
                                >
                                    {loading ? (
                                        <CircularProgress size={20} color="inherit" />
                                    ) : (
                                        <>
                                            <Icon sx={{ mr: 1 }}>settings_applications</Icon>
                                            Processar Fila
                                        </>
                                    )}
                                </MDButton>
                            </Grid>
                        </Grid>
                    </Grid>
                </Card>

                {/* ===== PAINEL INFORMATIVO: REGRA DA FILA vs. PROCESSAR FILA ===== */}
                {selectedRegra && (
                    <Card 
                        variant="outlined" 
                        sx={{ 
                            p: 2, 
                            mb: 3, 
                            borderRadius: "12px", 
                            background: darkMode ? ESCURO.painel : "#f8fafc", 
                            borderColor: darkMode ? "rgba(255, 255, 255, 0.15)" : "#cbd5e1" 
                        }}
                    >
                        <MDBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                            <MDBox display="flex" alignItems="center" gap={1.5}>
                                <Avatar sx={{ bgcolor: darkMode ? ESCURO.painelEscuro : "#e3f2fd", color: "#0288d1", width: 42, height: 42 }}>
                                    <Icon>insights</Icon>
                                </Avatar>
                                <MDBox>
                                    <MDBox display="flex" alignItems="center" gap={1}>
                                        <MDTypography variant="h6" fontWeight="bold" sx={{ color: darkMode ? "#ffffff" : "#0f172a" }}>
                                            Informativo da Regra: {selectedRegra.nome || selectedRegra.ccNome}
                                        </MDTypography>
                                        {loadingPreview && <CircularProgress size={16} color="info" />}
                                    </MDBox>
                                    <MDTypography variant="caption" sx={{ color: darkMode ? "#cbd5e1" : "#64748b", fontSize: "12.5px" }}>
                                        Comparativo detalhado entre a <strong>Base Bruta da Regra</strong> e o <strong>Processamento Real do Dia</strong>.
                                    </MDTypography>
                                </MDBox>
                            </MDBox>

                            <Chip 
                                icon={<Icon style={{ fontSize: "15px" }}>info</Icon>}
                                label="Entenda Regra vs. Processar"
                                color="info"
                                variant="outlined"
                                size="small"
                                sx={{ fontWeight: "bold" }}
                            />
                        </MDBox>

                        {previewData ? (
                            <Grid container spacing={2} mt={1} alignItems="stretch">
                                {/* 1. Total Bruto na Regra */}
                                <Grid item xs={12} sm={4} display="flex">
                                    <MDBox 
                                        p={2} 
                                        borderRadius="10px" 
                                        width="100%"
                                        display="flex"
                                        flexDirection="column"
                                        justifyContent="space-between"
                                        sx={{ bgcolor: darkMode ? ESCURO.painelEscuro : "#ffffff", border: `1px solid ${darkMode ? "rgba(255, 255, 255, 0.1)" : "#e2e8f0"}` }}
                                    >
                                        <MDBox>
                                            <MDTypography variant="caption" fontWeight="bold" sx={{ color: darkMode ? "#cbd5e1" : "#64748b", display: "block", fontSize: "11px", mb: 0.5 }}>
                                                1. Total na Regra da Fila (Base Bruta)
                                            </MDTypography>
                                            <MDTypography variant="h4" fontWeight="bold" sx={{ color: darkMode ? "#ffffff" : "#0f172a" }}>
                                                {previewData.totalBaseRegra || 0} <MDTypography variant="caption" sx={{ fontSize: "12px", color: darkMode ? "#cbd5e1" : "#64748b" }}>fichas ativas com saldo</MDTypography>
                                            </MDTypography>
                                        </MDBox>
                                        <MDTypography variant="caption" sx={{ color: darkMode ? "#94a3b8" : "#64748b", fontSize: "10.5px", display: "block", mt: 1 }}>
                                            Total da base que atende estritamente aos filtros da regra (Sem travas de agendamento futuro).
                                        </MDTypography>
                                    </MDBox>
                                </Grid>

                                {/* 2. Ignoradas / Retidas por Agendamento Futuro */}
                                <Grid item xs={12} sm={4} display="flex">
                                    <MDBox 
                                        p={2} 
                                        borderRadius="10px" 
                                        width="100%"
                                        display="flex"
                                        flexDirection="column"
                                        justifyContent="space-between"
                                        sx={{ bgcolor: darkMode ? ESCURO.painelEscuro : "#ffffff", border: `1px solid ${darkMode ? "rgba(255, 255, 255, 0.1)" : "#e2e8f0"}` }}
                                    >
                                        <MDBox>
                                            <MDTypography variant="caption" fontWeight="bold" sx={{ color: "#f97316", display: "block", fontSize: "11px", mb: 0.5 }}>
                                                2. Retidas / Ignoradas no Dia (Agendamento Futuro)
                                            </MDTypography>
                                            <MDTypography variant="h4" fontWeight="bold" sx={{ color: "#f97316" }}>
                                                -{previewData.fichasAgendamentoFuturo || 0} <MDTypography variant="caption" sx={{ fontSize: "12px", color: darkMode ? "#cbd5e1" : "#64748b" }}>fichas agendadas</MDTypography>
                                            </MDTypography>
                                        </MDBox>
                                        <MDTypography variant="caption" sx={{ color: darkMode ? "#94a3b8" : "#64748b", fontSize: "10.5px", display: "block", mt: 1 }}>
                                            {respeitarAgendamento 
                                                ? "Fichas agendadas para datas futuras (amanhã/depois) que foram preservadas para não quebrar a agenda." 
                                                : "Trava desativada: Fichas com agendamento futuro não estão sendo ignoradas."}
                                        </MDTypography>
                                    </MDBox>
                                </Grid>

                                {/* 3. Elegíveis para Fila Hoje */}
                                <Grid item xs={12} sm={4} display="flex">
                                    <MDBox 
                                        p={2} 
                                        borderRadius="10px" 
                                        width="100%"
                                        display="flex"
                                        flexDirection="column"
                                        justifyContent="space-between"
                                        sx={{ bgcolor: darkMode ? ESCURO.painelEscuro : "#ffffff", border: `1px solid ${darkMode ? "rgba(255, 255, 255, 0.1)" : "#e2e8f0"}` }}
                                    >
                                        <MDBox>
                                            <MDTypography variant="caption" fontWeight="bold" sx={{ color: "#22c55e", display: "block", fontSize: "11px", mb: 0.5 }}>
                                                3. Elegíveis para Processar Fila Hoje
                                            </MDTypography>
                                            <MDTypography variant="h4" fontWeight="bold" sx={{ color: "#22c55e" }}>
                                                {previewData.fichasElegiveis || 0} <MDTypography variant="caption" sx={{ fontSize: "12px", color: darkMode ? "#cbd5e1" : "#64748b" }}>fichas prontas</MDTypography>
                                            </MDTypography>
                                        </MDBox>
                                        <MDTypography variant="caption" sx={{ color: darkMode ? "#94a3b8" : "#64748b", fontSize: "10.5px", display: "block", mt: 1 }}>
                                            Quantidade pronta para entrar na ordenação e distribuição imediata nos operadores selecionados.
                                        </MDTypography>
                                    </MDBox>
                                </Grid>
                            </Grid>
                        ) : (
                            <MDBox mt={1.5}>
                                <MDTypography variant="caption" sx={{ color: darkMode ? "#cbd5e1" : "#64748b", fontSize: "12px" }}>
                                    {selectedOperadores.length === 0 
                                        ? "Selecione ao menos um operador destino acima para visualizar a contagem exata da base e o que será processado." 
                                        : "Carregando raio-x da base..."}
                                </MDTypography>
                            </MDBox>
                        )}

                        <MDBox mt={2} p={1.5} borderRadius="8px" sx={{ bgcolor: darkMode ? ESCURO.painelEscuro : "#eef2ff", border: `1px solid ${darkMode ? "rgba(255, 255, 255, 0.1)" : "#c7d2fe"}` }}>
                            <MDTypography variant="caption" sx={{ color: darkMode ? "#ffffff" : "#1e1b4b", fontSize: "11.5px", display: "flex", alignItems: "center", gap: 1 }}>
                                <Icon sx={{ color: "#6366f1", fontSize: "16px !important" }}>lightbulb</Icon>
                                <span>
                                    <strong>Resumo da Regra vs. Processamento:</strong> A <i>Regra da Fila</i> reflete todos os registros da base ativa com saldo que batem com o filtro. O <i>Processar Fila</i> aplica as travas de execução do dia (ex: <i>Respeitar agendamento futuro</i> retém os casos futuros no dia da execução).
                                </span>
                            </MDTypography>
                        </MDBox>
                    </Card>
                )}

                {/* ===== PAINEL DE RESULTADO DA DISTRIBUIÇÃO (PADRÃO DE CORES DO SISTEMA DARK/LIGHT) ===== */}
                {resultados && resultados.length > 0 && (
                    <Card 
                        variant="outlined" 
                        sx={{ 
                            p: 2, 
                            borderRadius: "14px", 
                            background: darkMode ? ESCURO.cartao : "#ffffff", 
                            borderColor: darkMode ? "rgba(255, 255, 255, 0.12)" : "#e2e8f0",
                            boxShadow: darkMode ? "0 10px 30px rgba(0,0,0,0.3)" : "0 4px 20px rgba(0,0,0,0.05)"
                        }}
                    >
                        {/* 1. Header Superior da Distribuição */}
                        <MDBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={3}>
                            <MDBox display="flex" alignItems="center" gap={1.8}>
                                <Avatar sx={{ bgcolor: darkMode ? ESCURO.painel : "#e0f2fe", color: darkMode ? "#90caf9" : "#0288d1", width: 46, height: 46 }}>
                                    <Icon fontSize="medium">groups</Icon>
                                </Avatar>
                                <MDBox>
                                    <MDTypography variant="h5" fontWeight="bold" sx={{ color: darkMode ? "#ffffff" : "#0f172a" }}>
                                        Resultado da Distribuição
                                    </MDTypography>
                                    <MDTypography variant="caption" sx={{ color: darkMode ? "#cbd5e1" : "#64748b", fontSize: "13px" }}>
                                        Veja o resumo da distribuição de fichas por operador e o detalhamento por status.
                                    </MDTypography>
                                </MDBox>
                            </MDBox>

                            {/* Widgets à direita: Data, Responsável, Botão Exportar */}
                            <MDBox display="flex" alignItems="center" flexWrap="wrap" gap={2}>
                                <MDBox 
                                    display="flex" 
                                    alignItems="center" 
                                    gap={1} 
                                    px={1.8} 
                                    py={0.8} 
                                    borderRadius="10px" 
                                    sx={{ bgcolor: darkMode ? ESCURO.painel : "#f8fafc", border: `1px solid ${darkMode ? "rgba(255, 255, 255, 0.1)" : "#e2e8f0"}` }}
                                >
                                    <Icon sx={{ color: darkMode ? "#cbd5e1" : "#64748b", fontSize: "18px !important" }}>calendar_today</Icon>
                                    <MDBox>
                                        <MDTypography variant="caption" sx={{ color: darkMode ? "#cbd5e1" : "#64748b", fontSize: "10px", display: "block" }}>
                                            Data da distribuição
                                        </MDTypography>
                                        <MDTypography variant="caption" fontWeight="bold" sx={{ color: darkMode ? "#ffffff" : "#1e293b", fontSize: "12px" }}>
                                            {dataDisp}
                                        </MDTypography>
                                    </MDBox>
                                </MDBox>

                                <MDBox 
                                    display="flex" 
                                    alignItems="center" 
                                    gap={1} 
                                    px={1.8} 
                                    py={0.8} 
                                    borderRadius="10px" 
                                    sx={{ bgcolor: darkMode ? ESCURO.painel : "#f8fafc", border: `1px solid ${darkMode ? "rgba(255, 255, 255, 0.1)" : "#e2e8f0"}` }}
                                >
                                    <Icon sx={{ color: darkMode ? "#cbd5e1" : "#64748b", fontSize: "18px !important" }}>person_outline</Icon>
                                    <MDBox>
                                        <MDTypography variant="caption" sx={{ color: darkMode ? "#cbd5e1" : "#64748b", fontSize: "10px", display: "block" }}>
                                            Responsável
                                        </MDTypography>
                                        <MDTypography variant="caption" fontWeight="bold" sx={{ color: darkMode ? "#ffffff" : "#1e293b", fontSize: "12px" }}>
                                            {respNome}
                                        </MDTypography>
                                    </MDBox>
                                </MDBox>

                                <MDButton
                                    variant="gradient"
                                    color="info"
                                    size="small"
                                    onClick={(e) => setExportAnchorEl(e.currentTarget)}
                                    sx={{ height: 38, px: 2, textTransform: "none", fontWeight: "bold" }}
                                >
                                    <Icon sx={{ mr: 0.5 }}>file_download</Icon> Exportar <Icon sx={{ ml: 0.5 }}>keyboard_arrow_down</Icon>
                                </MDButton>
                                <Menu
                                    anchorEl={exportAnchorEl}
                                    open={Boolean(exportAnchorEl)}
                                    onClose={() => setExportAnchorEl(null)}
                                    PaperProps={{
                                        sx: {
                                            bgcolor: darkMode ? ESCURO.painel : "#ffffff",
                                            color: darkMode ? "#ffffff" : "#333333",
                                            border: `1px solid ${darkMode ? "rgba(255, 255, 255, 0.15)" : "#e0e0e0"}`
                                        }
                                    }}
                                >
                                    <MenuItem onClick={handleExportCSV} sx={{ fontSize: "13px" }}>
                                        <Icon sx={{ mr: 1, fontSize: "18px !important" }}>table_chart</Icon> Exportar para CSV
                                    </MenuItem>
                                </Menu>
                            </MDBox>
                        </MDBox>

                        {/* 2. Cinco Cards de Resumo Geral (KPIs Consolidados do Topo) */}
                        <Grid container spacing={2} mb={4}>
                            <Grid item xs={12} sm={6} md={2.4}>
                                <MDBox 
                                    p={2} 
                                    borderRadius="12px" 
                                    sx={{ 
                                        bgcolor: darkMode ? ESCURO.painel : "#f8fafc", 
                                        border: `1px solid ${darkMode ? "rgba(255, 255, 255, 0.1)" : "#e2e8f0"}`,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.5
                                    }}
                                >
                                    <Avatar sx={{ bgcolor: darkMode ? ESCURO.painelEscuro : "#e3f2fd", color: "#3b82f6", width: 44, height: 44 }}>
                                        <Icon>groups</Icon>
                                    </Avatar>
                                    <MDBox>
                                        <MDTypography variant="caption" fontWeight="bold" sx={{ color: darkMode ? "#cbd5e1" : "#64748b", display: "block", fontSize: "11px" }}>
                                            Operadores
                                        </MDTypography>
                                        <MDTypography variant="h4" fontWeight="bold" sx={{ color: darkMode ? "#ffffff" : "#0f172a" }}>
                                            {summaryKPIs.totalOperadores}
                                        </MDTypography>
                                        <MDTypography variant="caption" sx={{ color: darkMode ? "#94a3b8" : "#94a3b8", fontSize: "10px", display: "block" }}>
                                            participaram da distribuição
                                        </MDTypography>
                                    </MDBox>
                                </MDBox>
                            </Grid>

                            <Grid item xs={12} sm={6} md={2.4}>
                                <MDBox 
                                    p={2} 
                                    borderRadius="12px" 
                                    sx={{ 
                                        bgcolor: darkMode ? ESCURO.painel : "#f8fafc", 
                                        border: `1px solid ${darkMode ? "rgba(255, 255, 255, 0.1)" : "#e2e8f0"}`,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.5
                                    }}
                                >
                                    <Avatar sx={{ bgcolor: darkMode ? ESCURO.painelEscuro : "#e0f7fa", color: "#06b6d4", width: 44, height: 44 }}>
                                        <Icon>description</Icon>
                                    </Avatar>
                                    <MDBox>
                                        <MDTypography variant="caption" fontWeight="bold" sx={{ color: darkMode ? "#cbd5e1" : "#64748b", display: "block", fontSize: "11px" }}>
                                            Fila Anterior (Total)
                                        </MDTypography>
                                        <MDTypography variant="h4" fontWeight="bold" sx={{ color: darkMode ? "#ffffff" : "#0f172a" }}>
                                            {summaryKPIs.totalAntes}
                                        </MDTypography>
                                        <MDTypography variant="caption" sx={{ color: darkMode ? "#94a3b8" : "#94a3b8", fontSize: "10px", display: "block" }}>
                                            fichas na fila dos operadores
                                        </MDTypography>
                                    </MDBox>
                                </MDBox>
                            </Grid>

                            <Grid item xs={12} sm={6} md={2.4}>
                                <MDBox 
                                    p={2} 
                                    borderRadius="12px" 
                                    sx={{ 
                                        bgcolor: darkMode ? ESCURO.painel : "#f8fafc", 
                                        border: `1px solid ${darkMode ? "rgba(255, 255, 255, 0.1)" : "#e2e8f0"}`,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.5
                                    }}
                                >
                                    <Avatar sx={{ bgcolor: darkMode ? "#4a2525" : "#ffebee", color: "#ef5350", width: 44, height: 44 }}>
                                        <Icon>arrow_downward</Icon>
                                    </Avatar>
                                    <MDBox>
                                        <MDTypography variant="caption" fontWeight="bold" sx={{ color: darkMode ? "#cbd5e1" : "#64748b", display: "block", fontSize: "11px" }}>
                                            Fichas Retiradas (Total)
                                        </MDTypography>
                                        <MDTypography variant="h4" fontWeight="bold" sx={{ color: "#ef5350" }}>
                                            -{summaryKPIs.totalRetirados}
                                        </MDTypography>
                                        <MDTypography variant="caption" sx={{ color: darkMode ? "#94a3b8" : "#94a3b8", fontSize: "10px", display: "block" }}>
                                            retiradas da fila dos operadores
                                        </MDTypography>
                                    </MDBox>
                                </MDBox>
                            </Grid>

                            <Grid item xs={12} sm={6} md={2.4}>
                                <MDBox 
                                    p={2} 
                                    borderRadius="12px" 
                                    sx={{ 
                                        bgcolor: darkMode ? ESCURO.painel : "#f8fafc", 
                                        border: `1px solid ${darkMode ? "rgba(255, 255, 255, 0.1)" : "#e2e8f0"}`,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.5
                                    }}
                                >
                                    <Avatar sx={{ bgcolor: darkMode ? "#1b4332" : "#e8f5e9", color: "#22c55e", width: 44, height: 44 }}>
                                        <Icon>arrow_upward</Icon>
                                    </Avatar>
                                    <MDBox>
                                        <MDTypography variant="caption" fontWeight="bold" sx={{ color: darkMode ? "#cbd5e1" : "#64748b", display: "block", fontSize: "11px" }}>
                                            Fichas Adicionadas (Total)
                                        </MDTypography>
                                        <MDTypography variant="h4" fontWeight="bold" sx={{ color: "#22c55e" }}>
                                            +{summaryKPIs.totalAdicionados}
                                        </MDTypography>
                                        <MDTypography variant="caption" sx={{ color: darkMode ? "#94a3b8" : "#94a3b8", fontSize: "10px", display: "block" }}>
                                            adicionadas para os operadores
                                        </MDTypography>
                                    </MDBox>
                                </MDBox>
                            </Grid>

                            <Grid item xs={12} sm={6} md={2.4}>
                                <MDBox 
                                    p={2} 
                                    borderRadius="12px" 
                                    sx={{ 
                                        bgcolor: darkMode ? ESCURO.painel : "#f8fafc", 
                                        border: `1px solid ${darkMode ? "rgba(255, 255, 255, 0.1)" : "#e2e8f0"}`,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.5
                                    }}
                                >
                                    <Avatar sx={{ bgcolor: darkMode ? ESCURO.painelEscuro : "#f3e5f5", color: darkMode ? corRoxo(true) : "#a855f7", width: 44, height: 44 }}>
                                        <Icon>storage</Icon>
                                    </Avatar>
                                    <MDBox>
                                        <MDTypography variant="caption" fontWeight="bold" sx={{ color: darkMode ? "#cbd5e1" : "#64748b", display: "block", fontSize: "11px" }}>
                                            Fila Atual (Total)
                                        </MDTypography>
                                        <MDTypography variant="h4" fontWeight="bold" sx={{ color: darkMode ? corRoxo(true) : "#7b1fa2" }}>
                                            {summaryKPIs.totalDepois}
                                        </MDTypography>
                                        <MDTypography variant="caption" sx={{ color: darkMode ? "#94a3b8" : "#94a3b8", fontSize: "10px", display: "block" }}>
                                            fichas na fila dos operadores
                                        </MDTypography>
                                    </MDBox>
                                </MDBox>
                            </Grid>
                        </Grid>

                        {/* 3. Barra de Controle da Tabela de Operadores */}
                        <MDBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={2}>
                            <MDBox display="flex" alignItems="center" gap={1}>
                                <Icon sx={{ color: darkMode ? "#90caf9" : "#1976d2", fontSize: "22px !important" }}>toc</Icon>
                                <MDTypography variant="h6" fontWeight="bold" sx={{ color: darkMode ? "#ffffff" : "#0f172a" }}>
                                    Distribuição por Operador
                                </MDTypography>
                            </MDBox>

                            <MDBox display="flex" alignItems="center" flexWrap="wrap" gap={2}>
                                {/* Campo de Busca */}
                                <TextField
                                    size="small"
                                    placeholder="Buscar operador..."
                                    value={searchFilter}
                                    onChange={(e) => { setSearchFilter(e.target.value); setPage(0); }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Icon sx={{ color: darkMode ? "#cbd5e1" : "#64748b" }}>search</Icon>
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{ minWidth: "220px" }}
                                />

                                {/* Campo de Ordenação */}
                                <MDBox display="flex" alignItems="center" gap={1}>
                                    <MDTypography variant="caption" sx={{ color: darkMode ? "#cbd5e1" : "#64748b", fontSize: "12px", whiteSpace: "nowrap" }}>
                                        Ordenar por
                                    </MDTypography>
                                    <Select
                                        size="small"
                                        value={sortBy}
                                        onChange={(e) => setSortBy(e.target.value)}
                                       
                                    >
                                        <MenuItem value="nome_asc">Nome (A - Z)</MenuItem>
                                        <MenuItem value="nome_desc">Nome (Z - A)</MenuItem>
                                        <MenuItem value="depois_desc">Fila Atual (Maior)</MenuItem>
                                        <MenuItem value="adicionados_desc">Fichas Adicionadas (Maior)</MenuItem>
                                        <MenuItem value="retirados_desc">Fichas Retiradas (Maior)</MenuItem>
                                    </Select>
                                </MDBox>
                            </MDBox>
                        </MDBox>

                        {/* 4. Tabela Interativa de Operadores (Linhas Expansíveis) */}
                        <TableContainer sx={{ borderRadius: "10px", border: `1px solid ${darkMode ? "rgba(255, 255, 255, 0.12)" : "#e2e8f0"}`, overflow: "hidden", mb: 2 }}>
                            <Table size="small" sx={{ tableLayout: "fixed", width: "100%", borderCollapse: "collapse" }}>
                                <MDBox 
                                    component="thead"
                                    sx={{ 
                                        display: "table-header-group", 
                                        width: "100%",
                                        bgcolor: darkMode ? ESCURO.painelEscuro : "#f1f5f9" 
                                    }}
                                >
                                    <TableRow 
                                        sx={{ 
                                            display: "table-row", 
                                            width: "100%",
                                            bgcolor: darkMode ? ESCURO.painelEscuro : "#f1f5f9" 
                                        }}
                                    >
                                        <TableCell width="40px" align="center" sx={{ py: 1.5, px: 0.5, borderBottom: `1px solid ${darkMode ? "rgba(255, 255, 255, 0.12)" : "#e2e8f0"}` }} />
                                        <TableCell width="50px" align="center" sx={{ color: darkMode ? "#cbd5e1" : "#475569", fontWeight: "bold", fontSize: "12px", py: 1.5, px: 1, borderBottom: `1px solid ${darkMode ? "rgba(255, 255, 255, 0.12)" : "#e2e8f0"}` }}>#</TableCell>
                                        <TableCell width="25%" sx={{ color: darkMode ? "#ffffff" : "#1e293b", fontWeight: "bold", fontSize: "12px", py: 1.5, px: 1.5, borderBottom: `1px solid ${darkMode ? "rgba(255, 255, 255, 0.12)" : "#e2e8f0"}` }}>Operador</TableCell>
                                        <TableCell width="11%" align="center" sx={{ color: darkMode ? "#ffffff" : "#1e293b", fontWeight: "bold", fontSize: "12px", py: 1.5, px: 1, borderBottom: `1px solid ${darkMode ? "rgba(255, 255, 255, 0.12)" : "#e2e8f0"}` }}>Fila Anterior</TableCell>
                                        <TableCell width="13%" align="center" sx={{ color: darkMode ? "#ff8a80" : "#d32f2f", fontWeight: "bold", fontSize: "12px", py: 1.5, px: 1, borderBottom: `1px solid ${darkMode ? "rgba(255, 255, 255, 0.12)" : "#e2e8f0"}` }}>Fichas Retiradas</TableCell>
                                        <TableCell width="13%" align="center" sx={{ color: darkMode ? "#a5d6a7" : "#15803d", fontWeight: "bold", fontSize: "12px", py: 1.5, px: 1, borderBottom: `1px solid ${darkMode ? "rgba(255, 255, 255, 0.12)" : "#e2e8f0"}` }}>Fichas Adicionadas</TableCell>
                                        <TableCell width="11%" align="center" sx={{ color: darkMode ? "#ffffff" : "#1e293b", fontWeight: "bold", fontSize: "12px", py: 1.5, px: 1, borderBottom: `1px solid ${darkMode ? "rgba(255, 255, 255, 0.12)" : "#e2e8f0"}` }}>Fila Atual</TableCell>
                                        <TableCell width="12%" align="center" sx={{ color: darkMode ? "#ffffff" : "#1e293b", fontWeight: "bold", fontSize: "12px", py: 1.5, px: 1, borderBottom: `1px solid ${darkMode ? "rgba(255, 255, 255, 0.12)" : "#e2e8f0"}` }}>Status</TableCell>
                                        <TableCell width="7%" align="center" sx={{ color: darkMode ? "#ffffff" : "#1e293b", fontWeight: "bold", fontSize: "12px", py: 1.5, px: 1, borderBottom: `1px solid ${darkMode ? "rgba(255, 255, 255, 0.12)" : "#e2e8f0"}` }}>Ações</TableCell>
                                    </TableRow>
                                </MDBox>
                                <TableBody sx={{ display: "table-row-group !important", width: "100%" }}>
                                    {paginatedResultados.map((r, idx) => {
                                        const actualIndex = page * rowsPerPage + idx + 1;
                                        const isExpanded = Boolean(expandedRows[idx]);
                                        const statusEntries = r.detalhesPorStatus ? Object.entries(r.detalhesPorStatus) : [];
                                        const totalStatusCount = statusEntries.reduce((a, b) => a + b[1], 0);

                                        return (
                                            <React.Fragment key={r.operador || idx}>
                                                {/* Linha Principal da Tabela */}
                                                <TableRow 
                                                    onClick={() => toggleRowExpand(idx)}
                                                    sx={{ 
                                                        display: "table-row !important",
                                                        width: "100%",
                                                        cursor: "pointer",
                                                        bgcolor: darkMode ? (isExpanded ? ESCURO.painelEscuro : (idx % 2 === 0 ? ESCURO.cartao : ESCURO.painel)) : (isExpanded ? "#f0f9ff" : (idx % 2 === 0 ? "#ffffff" : "#f8fafc")),
                                                        borderBottom: isExpanded ? "none" : `1px solid ${darkMode ? "rgba(255, 255, 255, 0.08)" : "#e2e8f0"}`,
                                                        "&:hover": { bgcolor: darkMode ? `${ESCURO.campo} !important` : "#f1f5f9 !important" },
                                                        transition: "all 0.15s ease"
                                                    }}
                                                >
                                                    <TableCell width="40px" align="center" sx={{ display: "table-cell !important", py: 1.2, px: 0.5 }}>
                                                        <IconButton size="small" sx={{ color: darkMode ? "#cbd5e1" : "#64748b", p: 0.5 }}>
                                                            <Icon>{isExpanded ? "keyboard_arrow_down" : "keyboard_arrow_right"}</Icon>
                                                        </IconButton>
                                                    </TableCell>
                                                    <TableCell width="50px" align="center" sx={{ display: "table-cell !important", color: darkMode ? "#cbd5e1" : "#64748b", fontSize: "12px", py: 1.2, px: 1 }}>
                                                        {actualIndex}
                                                    </TableCell>
                                                    <TableCell width="25%" sx={{ display: "table-cell !important", py: 1.2, px: 1.5 }}>
                                                        <MDTypography variant="button" fontWeight="bold" sx={{ color: darkMode ? "#ffffff" : "#0f172a", fontSize: "13px" }}>
                                                            {r.operador}
                                                        </MDTypography>
                                                    </TableCell>
                                                    <TableCell width="11%" align="center" sx={{ display: "table-cell !important", py: 1.2, px: 1 }}>
                                                        <MDTypography variant="button" fontWeight="medium" sx={{ color: darkMode ? "#ffffff" : "#334155", fontSize: "13px" }}>
                                                            {r.antes}
                                                        </MDTypography>
                                                    </TableCell>
                                                    <TableCell width="13%" align="center" sx={{ display: "table-cell !important", py: 1.2, px: 1 }}>
                                                        <MDTypography variant="button" fontWeight="bold" sx={{ color: "#ef5350", fontSize: "13px" }}>
                                                            -{r.retirados || 0}
                                                        </MDTypography>
                                                    </TableCell>
                                                    <TableCell width="13%" align="center" sx={{ display: "table-cell !important", py: 1.2, px: 1 }}>
                                                        <MDTypography variant="button" fontWeight="bold" sx={{ color: "#22c55e", fontSize: "13px" }}>
                                                            +{r.adicionados || 0}
                                                        </MDTypography>
                                                    </TableCell>
                                                    <TableCell width="11%" align="center" sx={{ display: "table-cell !important", py: 1.2, px: 1 }}>
                                                        <MDTypography variant="button" fontWeight="bold" sx={{ color: darkMode ? "#ffffff" : "#0f172a", fontSize: "13px" }}>
                                                            {r.depois}
                                                        </MDTypography>
                                                    </TableCell>
                                                    <TableCell width="12%" align="center" sx={{ display: "table-cell !important", py: 1.2, px: 1 }}>
                                                        <Chip 
                                                            icon={<Icon style={{ color: "#22c55e", fontSize: "14px" }}>check_circle</Icon>}
                                                            label="Concluído" 
                                                            size="small" 
                                                            sx={{ 
                                                                height: "22px", 
                                                                fontSize: "10.5px", 
                                                                fontWeight: "bold",
                                                                color: "#22c55e",
                                                                bgcolor: darkMode ? "rgba(34, 197, 94, 0.12)" : "#f0fdf4",
                                                                border: "1px solid #22c55e"
                                                            }} 
                                                        />
                                                    </TableCell>
                                                    <TableCell width="7%" align="center" sx={{ display: "table-cell !important", py: 1.2, px: 1 }} onClick={(e) => e.stopPropagation()}>
                                                        <IconButton size="small" sx={{ color: darkMode ? "#cbd5e1" : "#64748b" }}>
                                                            <Icon>more_vert</Icon>
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>

                                                {/* Painel Expansível da Linha (Detalhamento por Status + Cards) */}
                                                <TableRow sx={{ bgcolor: darkMode ? ESCURO.painelEscuro : "#f8fafc" }}>
                                                    <TableCell colSpan={9} sx={{ p: 0, border: "none" }}>
                                                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                                            <MDBox p={2.5} sx={{ borderBottom: `1px solid ${darkMode ? "rgba(255, 255, 255, 0.1)" : "#e2e8f0"}` }}>
                                                                <Grid container spacing={3}>
                                                                    {/* Lado Esquerdo: Resumo do Operador (4 Cards) */}
                                                                    <Grid item xs={12} md={5}>
                                                                        <MDTypography variant="button" fontWeight="bold" sx={{ color: darkMode ? "#ffffff" : "#0f172a", display: "block", mb: 1.5 }}>
                                                                            Resumo do Operador
                                                                        </MDTypography>
                                                                        <Grid container spacing={1.5}>
                                                                            <Grid item xs={6}>
                                                                                <MDBox p={2} borderRadius="10px" sx={{ bgcolor: darkMode ? ESCURO.painel : "#ffffff", border: `1px solid ${darkMode ? "rgba(255, 255, 255, 0.1)" : "#e2e8f0"}` }}>
                                                                                    <MDTypography variant="caption" sx={{ color: darkMode ? "#cbd5e1" : "#64748b", display: "block", fontSize: "11px", mb: 0.5 }}>
                                                                                        Fila Anterior
                                                                                    </MDTypography>
                                                                                    <MDTypography variant="h5" fontWeight="bold" sx={{ color: darkMode ? "#ffffff" : "#0f172a" }}>
                                                                                        {r.antes}
                                                                                    </MDTypography>
                                                                                </MDBox>
                                                                            </Grid>
                                                                            <Grid item xs={6}>
                                                                                <MDBox p={2} borderRadius="10px" sx={{ bgcolor: darkMode ? ESCURO.painel : "#ffffff", border: `1px solid ${darkMode ? "rgba(255, 255, 255, 0.1)" : "#e2e8f0"}` }}>
                                                                                    <MDTypography variant="caption" sx={{ color: darkMode ? "#cbd5e1" : "#64748b", display: "block", fontSize: "11px", mb: 0.5 }}>
                                                                                        Fichas Retiradas
                                                                                    </MDTypography>
                                                                                    <MDTypography variant="h5" fontWeight="bold" sx={{ color: "#ef5350" }}>
                                                                                        -{r.retirados || 0}
                                                                                    </MDTypography>
                                                                                </MDBox>
                                                                            </Grid>
                                                                            <Grid item xs={6}>
                                                                                <MDBox p={2} borderRadius="10px" sx={{ bgcolor: darkMode ? ESCURO.painel : "#ffffff", border: `1px solid ${darkMode ? "rgba(255, 255, 255, 0.1)" : "#e2e8f0"}` }}>
                                                                                    <MDTypography variant="caption" sx={{ color: darkMode ? "#cbd5e1" : "#64748b", display: "block", fontSize: "11px", mb: 0.5 }}>
                                                                                        Fichas Adicionadas
                                                                                    </MDTypography>
                                                                                    <MDTypography variant="h5" fontWeight="bold" sx={{ color: "#22c55e" }}>
                                                                                        +{r.adicionados || 0}
                                                                                    </MDTypography>
                                                                                </MDBox>
                                                                            </Grid>
                                                                            <Grid item xs={6}>
                                                                                <MDBox p={2} borderRadius="10px" sx={{ bgcolor: darkMode ? ESCURO.painel : "#ffffff", border: `1px solid ${darkMode ? "rgba(255, 255, 255, 0.1)" : "#e2e8f0"}` }}>
                                                                                    <MDTypography variant="caption" sx={{ color: darkMode ? "#cbd5e1" : "#64748b", display: "block", fontSize: "11px", mb: 0.5 }}>
                                                                                        Fila Atual
                                                                                    </MDTypography>
                                                                                    <MDTypography variant="h5" fontWeight="bold" sx={{ color: darkMode ? "#ffffff" : "#0f172a" }}>
                                                                                        {r.depois}
                                                                                    </MDTypography>
                                                                                </MDBox>
                                                                            </Grid>
                                                                        </Grid>
                                                                    </Grid>

                                                                    {/* Lado Direito: Distribuição por Status com Barras de Progresso */}
                                                                    <Grid item xs={12} md={7}>
                                                                        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                                                                            <MDTypography variant="button" fontWeight="bold" sx={{ color: darkMode ? "#ffffff" : "#0f172a" }}>
                                                                                Distribuição por Status
                                                                            </MDTypography>
                                                                            <MDBox display="flex" borderRadius="6px" overflow="hidden" border={`1px solid ${darkMode ? "rgba(255, 255, 255, 0.15)" : "#cbd5e1"}`}>
                                                                                <MDBox px={1.2} py={0.3} sx={{ bgcolor: darkMode ? "#007bff" : "#1976d2", color: "#ffffff", fontSize: "10px", fontWeight: "bold" }}>
                                                                                    Quantidade
                                                                                </MDBox>
                                                                                <MDBox px={1.2} py={0.3} sx={{ bgcolor: darkMode ? ESCURO.painel : "#f1f5f9", color: darkMode ? "#cbd5e1" : "#64748b", fontSize: "10px", fontWeight: "medium" }}>
                                                                                    Percentual
                                                                                </MDBox>
                                                                            </MDBox>
                                                                        </MDBox>

                                                                        {statusEntries.length > 0 ? (
                                                                            <MDBox display="flex" flexDirection="column" gap={1.2}>
                                                                                {statusEntries.map(([statusNome, qtd], sIdx) => {
                                                                                    const pct = totalStatusCount > 0 ? ((qtd / totalStatusCount) * 100).toFixed(1) : 0;
                                                                                    const barColor = STATUS_COLORS[sIdx % STATUS_COLORS.length];

                                                                                    return (
                                                                                        <Grid container key={statusNome} alignItems="center" spacing={1}>
                                                                                            <Grid item xs={3.5}>
                                                                                                <MDTypography variant="caption" fontWeight="medium" sx={{ color: darkMode ? "#e2e8f0" : "#334155", fontSize: "11.5px" }} noWrap>
                                                                                                    {statusNome}
                                                                                                </MDTypography>
                                                                                            </Grid>
                                                                                            <Grid item xs={5.5}>
                                                                                                <MDBox sx={{ width: "100%", height: 8, bgcolor: darkMode ? ESCURO.painel : "#e2e8f0", borderRadius: 4, overflow: "hidden" }}>
                                                                                                    <MDBox sx={{ width: `${pct}%`, height: "100%", bgcolor: barColor, borderRadius: 4, transition: "width 0.4s ease" }} />
                                                                                                </MDBox>
                                                                                            </Grid>
                                                                                            <Grid item xs={1.5} textAlign="right">
                                                                                                <MDTypography variant="caption" fontWeight="bold" sx={{ color: darkMode ? "#ffffff" : "#0f172a", fontSize: "11.5px" }}>
                                                                                                    {qtd}
                                                                                                </MDTypography>
                                                                                            </Grid>
                                                                                            <Grid item xs={1.5} textAlign="right">
                                                                                                <MDTypography variant="caption" sx={{ color: darkMode ? "#cbd5e1" : "#64748b", fontSize: "11px" }}>
                                                                                                    {pct}%
                                                                                                </MDTypography>
                                                                                            </Grid>
                                                                                        </Grid>
                                                                                    );
                                                                                })}

                                                                                {/* Linha de Total */}
                                                                                <MDBox display="flex" justifyContent="space-between" alignItems="center" mt={1} pt={1} borderTop={`1px solid ${darkMode ? "rgba(255, 255, 255, 0.1)" : "#e2e8f0"}`}>
                                                                                    <MDTypography variant="caption" fontWeight="bold" sx={{ color: darkMode ? "#ffffff" : "#0f172a" }}>
                                                                                        Total
                                                                                    </MDTypography>
                                                                                    <MDBox display="flex" gap={3}>
                                                                                        <MDTypography variant="caption" fontWeight="bold" sx={{ color: darkMode ? "#ffffff" : "#0f172a" }}>
                                                                                            {totalStatusCount}
                                                                                        </MDTypography>
                                                                                        <MDTypography variant="caption" fontWeight="bold" sx={{ color: darkMode ? "#ffffff" : "#0f172a" }}>
                                                                                            100%
                                                                                        </MDTypography>
                                                                                    </MDBox>
                                                                                </MDBox>
                                                                            </MDBox>
                                                                        ) : (
                                                                            <MDTypography variant="caption" sx={{ color: darkMode ? "#cbd5e1" : "#64748b" }}>
                                                                                Nenhum detalhamento de status disponível.
                                                                            </MDTypography>
                                                                        )}
                                                                    </Grid>
                                                                </Grid>
                                                            </MDBox>
                                                        </Collapse>
                                                    </TableCell>
                                                </TableRow>
                                            </React.Fragment>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* 5. Rodapé da Tabela: Paginação e Contadores estilo Mockup */}
                        <MDBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} pt={1}>
                            <MDBox display="flex" alignItems="center" gap={1}>
                                <MDTypography variant="caption" sx={{ color: darkMode ? "#cbd5e1" : "#64748b", fontSize: "12px" }}>
                                    Mostrando {paginatedResultados.length} de {filteredAndSortedResultados.length} operadores
                                </MDTypography>
                                <Tooltip title="Lista completa dos operadores afetados pelo processamento de fila">
                                    <Icon sx={{ color: darkMode ? "#cbd5e1" : "#64748b", fontSize: "15px !important", cursor: "help" }}>info_outline</Icon>
                                </Tooltip>
                            </MDBox>

                            <MDBox display="flex" alignItems="center" gap={2}>
                                <MDBox display="flex" alignItems="center" gap={1}>
                                    <MDTypography variant="caption" sx={{ color: darkMode ? "#cbd5e1" : "#64748b", fontSize: "12px", whiteSpace: "nowrap" }}>
                                        Itens por página
                                    </MDTypography>
                                    <Select
                                        size="small"
                                        value={rowsPerPage}
                                        onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
                                       
                                    >
                                        <MenuItem value={10}>10</MenuItem>
                                        <MenuItem value={25}>25</MenuItem>
                                        <MenuItem value={50}>50</MenuItem>
                                        <MenuItem value={-1}>Todos</MenuItem>
                                    </Select>
                                </MDBox>

                                {rowsPerPage !== -1 && filteredAndSortedResultados.length > rowsPerPage && (
                                    <Pagination
                                        count={Math.ceil(filteredAndSortedResultados.length / rowsPerPage)}
                                        page={page + 1}
                                        onChange={(e, newPage) => setPage(newPage - 1)}
                                        size="small"
                                        color="primary"
                                        sx={{
                                            "& .MuiPaginationItem-root": {
                                                color: darkMode ? "#cbd5e1" : "#64748b",
                                                "&.Mui-selected": {
                                                    bgcolor: darkMode ? "#007bff !important" : "#1976d2 !important",
                                                    color: "#ffffff !important"
                                                }
                                            }
                                        }}
                                    />
                                )}
                            </MDBox>
                        </MDBox>
                    </Card>
                )}
            </Grid>

            <MDSnackbar
                color={successSB ? "success" : "error"}
                icon={successSB ? "check" : "warning"}
                title={successSB ? "Sucesso" : "Erro"}
                content={sbMessage}
                dateTime=""
                open={successSB || errorSB}
                onClose={() => { setSuccessSB(false); setErrorSB(false); }}
                close={() => { setSuccessSB(false); setErrorSB(false); }}
                bgWhite
            />

            <MDAuditModal
                open={openAuditModal}
                onClose={() => setOpenAuditModal(false)}
                title="Auditoria de Distribuição de Filas"
                loading={loadingAudit}
                logs={auditLogs}
                entityLabel="Regra"
            />
        </Grid>
    );
}
